import React, { useState, useEffect, useMemo } from "react";
import { useMediaQuery } from "react-responsive";

import { useSelector, useDispatch } from "react-redux";
import TrainModal from "../TrainModal/TrainModal";
import { useNavigate } from "react-router-dom";

import {
  TrainContainer,
  TrainGrid,
  TrainModelCard,
  CardTitle,
  ModelCard,
  ModelTitle,
  EmptyState,
  Button,
  Column,
  CardText,
  //   CardLink,
  ButtonContainer,
  TextContainer,
  BannerTextContainer,
  BannerContainer,
  BannerTitle,
  BannerButton,
  BannerListItem,
  BannerList,
  BannerRow,
  BannerColumn,
  BannerSubtitle,
  BannerText,
  IconContainer,
  PaginationWrapper,
} from "./Train.styles";

import AiSvg from "../../assets/images/Glass.png";
// import { ReactComponent as AiSvg } from "../../assets/icons/aiGenerate.svg";
// import { ReactComponent as PreparingAnimation } from "../../assets/icons/preparing.svg";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import {
  selectTrainModels,
  selectCreateTrainModelLoading,
  setSelectedCollection,
  selectSelectedCollection,
  setTrainingPage,
} from "../../store/slices/model/slice";
import {
  getTrainModels,
  createTrainModel,
} from "../../store/slices/model/asyncThunks";
import TrainSkeletonLoader from "../utils/TrainSkeletonLoader";
import {
  BLOCK_USER_TO_TRAIN,
  TRAIN_MODEL_STATUS,
  TRAIN_MODEL_STATUS_TEXT,
} from "../../utils/constants";
import CollectionItem from "../utils/CollectionItem/CollectionItem";
import GenerateCollectionModal from "../GenerateCollectionModal/GenerateCollectionModal";
import gpuAnimation from "../../assets/animations/gpuIcon.json";
import LottieIcon from "../LottieIcon/LottieIcon";
import { selectUser } from "../../store/slices/user/slice";
import TrainVipModal from "../TrainVipModal/TrainVipModal";
import { ReactComponent as VideoSvg } from "../../assets/icons/video.svg";
import { ReactComponent as ImageSvg } from "../../assets/icons/image.svg";
import { isImageOutput } from "../../utils/utils";
import EditImageModal from "../EditImageModal/EditImageModal";
import Pagination from "../Pagination/Pagination";

const Train = ({ onLoginClick }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isTrainModalOpen, setIsTrainModalOpen] = useState(false);
  const [isOpenTrainVipModal, setIsOpenTrainVipModal] = useState(false);
  const isLoggedIn = useSelector(selectLoggedIn);
  const trainModels = useSelector(selectTrainModels);
  const user = useSelector(selectUser);
  const createTrainModelLoading = useSelector(selectCreateTrainModelLoading);

  const selectedCollection = useSelector(selectSelectedCollection);
  const [isEditImageModalOpen, setIsEditImageModalOpen] = useState(false);
  const [isGenerateCollectionModalOpen, setIsGenerateCollectionModalOpen] =
    useState(false);

  const isDesktop = useMediaQuery({ maxWidth: 1224 });
  const isTable = useMediaQuery({ maxWidth: 950 });
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const [dots, setDots] = useState("");

  const isUserBlocked = user && BLOCK_USER_TO_TRAIN.includes(user?._id);

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev.length < 3 ? prev + "." : ""));
    }, 500);

    return () => clearInterval(interval);
  }, []);

  const numOfColumns = useMemo(() => {
    switch (true) {
      case isMobile:
        return 1;
      case isTable:
        return 3;
      case isDesktop:
        return 4;
      default:
        return 5;
    }
  }, [isDesktop, isTable, isMobile]);

  const setPage = (newPage) => {
    dispatch(setTrainingPage(newPage));
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });
  };

  const handleGenerateAgain = (video) => {
    if (!video) return;

    const modelObject = {
      ...video,
      regenerate: false,
      collectionId: video._id,
    };

    dispatch(setSelectedCollection(modelObject));

    if (isImageOutput(modelObject.modelType ?? modelObject.modelName)) {
      setIsEditImageModalOpen(true);
    } else {
      setIsGenerateCollectionModalOpen(true);
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(getTrainModels({ page: trainModels.page ?? 1 }));
    }
  }, [isLoggedIn, dispatch, trainModels.page]);

  const handleTrainModelClick = () => {
    if (isUserBlocked) return;

    if (user.isVipCreator) {
      setIsOpenTrainVipModal(true);
    } else {
      setIsTrainModalOpen(true);
    }
  };

  const handleCloseTrainModal = () => {
    setIsTrainModalOpen(false);
  };

  const handleCloseTrainVipModal = () => {
    setIsOpenTrainVipModal(false);
  };

  const handleContinue = async (modelName, type) => {
    try {
      const result = await dispatch(
        createTrainModel({ name: modelName, type }),
      ).unwrap();

      const newModel = result.data.trainModel;
      dispatch(getTrainModels());

      setIsTrainModalOpen(false);

      navigate(`/train/${newModel.id}`);
    } catch (error) {
      console.log("Failed to create train model:", error);
    }
  };

  const handleModelClick = (model) => {
    if (isUserBlocked) return;

    if ([TRAIN_MODEL_STATUS.FAILED].includes(model.status)) {
      return;
    }
    if (
      [
        TRAIN_MODEL_STATUS.IN_PROGRESS,
        TRAIN_MODEL_STATUS.SUBMITTED_FOR_TRAINING,
        TRAIN_MODEL_STATUS.TRAINING,
        TRAIN_MODEL_STATUS.TRAINING_COMPLETED,
        TRAIN_MODEL_STATUS.GENERATING_COLLECTIONS,
      ].includes(model.status)
    ) {
      navigate(`/train/${model._id}`);

      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "instant",
      });
    } else if (
      [TRAIN_MODEL_STATUS.COLLECTIONS_CREATED].includes(model.status)
    ) {
      navigate(`/train/select-cover/${model._id}`);

      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "instant",
      });
    } else if (
      [
        TRAIN_MODEL_STATUS.COMPLETED,
        TRAIN_MODEL_STATUS.SUBMITTED_FOR_REVIEW,
        TRAIN_MODEL_STATUS.ACCEPTED,
        TRAIN_MODEL_STATUS.DECLINED,
      ].includes(model.status)
    ) {
      navigate(`/train/completed/${model._id}`);

      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "instant",
      });
    } else {
      navigate(`/train/${model._id}`);

      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "instant",
      });
    }
  };

  const openEditTrainModel = (modelId) => {
    navigate(`/train/edit/${modelId}`);

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });
  };

  //   const handleTutorialClick = () => {
  //     navigate("/train-tutorial");
  //     setTimeout(() => {
  //       window.scrollTo({
  //         top: 0,
  //         left: 0,
  //         behavior: "instant",
  //       });
  //     }, 300);
  //   };

  if (isLoggedIn === null) {
    return null;
  }

  if (isLoggedIn === false) {
    return (
      <TrainContainer>
        <EmptyState>
          <h3>Login Required</h3>
          <p>You need to be logged in to view training models.</p>
          <Button onClick={onLoginClick}>Login</Button>
        </EmptyState>
      </TrainContainer>
    );
  }

  if (trainModels.loading) {
    return (
      <TrainContainer>
        <TrainSkeletonLoader count={5} />
      </TrainContainer>
    );
  }

  const allItems = [
    {
      type: "create",
      component: (
        <TrainModelCard onClick={handleTrainModelClick}>
          <img src={AiSvg} width={"100%"} alt="Playbun.com generate model" />
          <TextContainer>
            <CardTitle>Train your own template</CardTitle>
            <CardText>
              Train your own templates and publish them to earn money
            </CardText>
          </TextContainer>
          <ButtonContainer>
            <Button style={{ width: "fit-content" }}>Train New Template</Button>
            {/* <CardLink onClick={handleTutorialClick}>Learn More</CardLink> */}
          </ButtonContainer>
        </TrainModelCard>
      ),
    },
    ...trainModels.data.map((model, i) => ({
      type: "model",
      data: model,
      index: i,
    })),
  ];

  return (
    <TrainContainer>
      {user?.invitedToVip && !user?.isVipCreator && (
        <BannerContainer>
          <BannerTextContainer>
            <BannerTitle>VIP Creators</BannerTitle>
            <BannerRow>
              <BannerColumn>
                <BannerSubtitle>VIP Creator Benefits:</BannerSubtitle>
                <BannerList>
                  <BannerListItem>
                    Discounted template cost: 700 instead of the 900
                  </BannerListItem>
                  <BannerListItem>
                    Pro subscription applied to your account
                  </BannerListItem>
                  <BannerListItem>
                    Dedicated section for "VIP Creators"
                  </BannerListItem>
                  <BannerListItem>
                    Payouts increase to 3-2-1 instead of 2-2-1
                  </BannerListItem>
                  <BannerListItem>
                    VIP Creator badge next to your name
                  </BannerListItem>
                  <BannerListItem>Invite to VIP Discord</BannerListItem>
                </BannerList>
              </BannerColumn>
              <BannerColumn>
                <BannerSubtitle>Requirements of VIP creators:</BannerSubtitle>
                <BannerList>
                  <BannerListItem>
                    Submit minimum 2 Playbun exclusive template per week, must
                    be only submitted to Playbun, must be high quality
                  </BannerListItem>
                  <BannerListItem>
                    Press apply below. Join the Discord and introduce yourself
                    in the Introductions channel 👋
                  </BannerListItem>
                </BannerList>
              </BannerColumn>
            </BannerRow>
          </BannerTextContainer>

          <BannerButton
            onClick={() => {
              if (user?.invitationLink) {
                window.open(user.invitationLink);
              }
            }}
          >
            Apply for VIP
          </BannerButton>
        </BannerContainer>
      )}

      {isUserBlocked && (
        <BannerContainer>
          <BannerTextContainer>
            <BannerTitle>Warning</BannerTitle>
            <BannerText>
              Your training has been restricted for 7 days as you have been
              copying templates of Playbun users. If copying of templates of our
              users continues your account will be permanently suspended.For any
              questions or inquiries, please contact us via email at:
              support@playbun.com.
            </BannerText>
          </BannerTextContainer>
        </BannerContainer>
      )}

      <TrainGrid>
        {Array.from({ length: numOfColumns }).map((_, colIndex) => (
          <Column key={colIndex}>
            {allItems
              .filter((_, i) => i % numOfColumns === colIndex)
              .map((item) => {
                if (item.type === "create") {
                  return <div key="create">{item.component}</div>;
                }

                const model = item.data;
                const i = item.index;

                if (
                  ![
                    TRAIN_MODEL_STATUS.ACCEPTED,
                    TRAIN_MODEL_STATUS.DECLINED,
                  ].includes(model.status) ||
                  !model.selectedCollection ||
                  !model.selectedCollection.input
                ) {
                  return (
                    <ModelCard
                      key={model._id + i}
                      onClick={() => handleModelClick(model)}
                    >
                      {model.type === "image" ? (
                        <IconContainer>
                          <ImageSvg />
                        </IconContainer>
                      ) : (
                        <IconContainer>
                          <VideoSvg />
                        </IconContainer>
                      )}

                      {[
                        TRAIN_MODEL_STATUS.SUBMITTED_FOR_TRAINING,
                        TRAIN_MODEL_STATUS.TRAINING,
                        TRAIN_MODEL_STATUS.TRAINING_COMPLETED,
                        TRAIN_MODEL_STATUS.GENERATING_COLLECTIONS,
                      ].includes(model.status) && (
                        <LottieIcon
                          animationData={gpuAnimation}
                          trigger="loop"
                          size={180}
                          style={{ margin: "-50px 0" }}
                        />
                      )}

                      <CardTitle>
                        {TRAIN_MODEL_STATUS_TEXT[model.status]}
                        {[
                          TRAIN_MODEL_STATUS.SUBMITTED_FOR_TRAINING,
                          TRAIN_MODEL_STATUS.TRAINING,
                          TRAIN_MODEL_STATUS.TRAINING_COMPLETED,
                          TRAIN_MODEL_STATUS.GENERATING_COLLECTIONS,
                          TRAIN_MODEL_STATUS.SUBMITTED_FOR_REVIEW,
                        ].includes(model.status) && <span>{dots}</span>}
                      </CardTitle>

                      <ModelTitle>{model.name}</ModelTitle>
                    </ModelCard>
                  );
                } else {
                  const collectionItem = model.selectedCollection;
                  return (
                    <CollectionItem
                      openEditTrainModel={() => openEditTrainModel(model._id)}
                      trainPage
                      key={collectionItem._id}
                      video={collectionItem}
                      onCollectionItemClick={() =>
                        handleGenerateAgain(collectionItem)
                      }
                    />
                  );
                }
              })}
          </Column>
        ))}
      </TrainGrid>
      <PaginationWrapper>
        <Pagination
          small={isMobile}
          currentPage={trainModels.page}
          totalCount={trainModels.total}
          perPage={19}
          onPageChange={(page) => setPage(page)}
        />
      </PaginationWrapper>

      <GenerateCollectionModal
        isOpen={isGenerateCollectionModalOpen}
        onClose={() => setIsGenerateCollectionModalOpen(false)}
      />

      <EditImageModal
        isOpen={isEditImageModalOpen}
        collection={selectedCollection}
        onClose={() => setIsEditImageModalOpen(false)}
      />

      <TrainVipModal
        isOpen={isOpenTrainVipModal}
        onClose={handleCloseTrainVipModal}
        onContinue={handleContinue}
      />

      <TrainModal
        isOpen={isTrainModalOpen}
        onClose={handleCloseTrainModal}
        onContinue={handleContinue}
        loading={createTrainModelLoading}
        user={user}
      />
    </TrainContainer>
  );
};

export default Train;
